import { AccessibleForm, Alert, Button, Col, Row } from '@digitools/honest-react';
import { isEqual } from 'lodash';
import React, { useContext, useEffect, useRef, useState } from 'react';
import { AuthenticationContext } from '../../../providers/AuthenticationProvider';
import { clearEmployerSubsLocs, getProducts, getSensitiveData, getStatusSensitiveData, isAllPermissionsMaxed, setUserPermissionState, setUserPermissionStateToFull, updateEmployerUser, } from '../api';
import { AdminPermissionContext } from '../context/AdminPermissionContext';
import { AccessLevelEnum } from '../types/permissionTypes';
import Crs from './Crs';
import EmployerSubsAndLocs from './EmployerSubsAndLocs';
import Products from './Products';
import SaveUndoButton from './SaveUndoButton';
import ViewStatus from './ViewStatus';
import useTealium from '../../../hooks/useTealium';
import { TEALIUM_EVENT, TEALIUM_PAGE_VIEW } from '../../../constants/tealiumConstants';
// Placeholder copy - wired as a constant for an easy swap once UX/legal/compliance finalise the wording.
const ALL_PERMISSIONS_DISCLAIMER = 'Enabling all permissions grants this user access to sensitive information. Future products, ' +
    'subsidiaries/locations, and sensitive data fields added to your configuration will be automatically ' +
    'included. New feature permissions are not automatically granted, and folder permissions are not included.';
const UnSavedData = ({ unSavedData, showDisclaimer }) => {
    return (<>
      {unSavedData ? (<Alert type={'warning'} className={'mt-3'}>
          <strong>Attention!</strong>&nbsp;You have unsaved changes.
          {showDisclaimer ? (<div className={'mt-2'} data-testid={'allPermissionsDisclaimer'}>
              {ALL_PERMISSIONS_DISCLAIMER}
            </div>) : null}
        </Alert>) : null}
    </>);
};
const EmployerPermissions = (props) => {
    const authContext = useContext(AuthenticationContext);
    const [productsList, setProductsList] = useState([]);
    const [crsSensitiveData, setCrsSensitiveData] = useState([]);
    const [statusSensitiveData, setStatusSensitiveData] = useState([]);
    const [userInformation, setUserInformation] = useState(() => JSON.parse(JSON.stringify(props.userInfo)));
    const [errorMessage, setErrorMessage] = useState('');
    const [showAlert, setShowAlert] = useState(false);
    const [clearAll, setClearAll] = useState(false);
    const [changed, setChanged] = useState(false);
    const [unSavedData, setUnSavedData] = useState(false);
    // True when a bulk Enable/Clear action has been applied but not yet saved.
    const [bulkChanged, setBulkChanged] = useState(false);
    // Drives the All Permissions Control label: maxed => "Clear All Permissions", otherwise "Enable All Permissions".
    const [allMaxed, setAllMaxed] = useState(() => props.userInfo && props.customerConfig
        ? isAllPermissionsMaxed(props.userInfo, props.customerConfig, props.permissions)
        : false);
    // Bumped on every form change; drives the post-commit re-derivation of the All Permissions Control label.
    const [changeTick, setChangeTick] = useState(0);
    const { trackEvent } = useTealium();
    const formApiRef = useRef();
    // Snapshot of the form's field values in their last clean (unedited / just-saved) state. Unsaved-state
    // is derived as a value-vs-baseline diff against this, so toggling a field and toggling it back clears
    // the banner. informed@3.14's own `dirty` can't be used: it is `!(empty(touched) && empty(values))`,
    // i.e. true the moment the form holds any value — which is immediately on mount once the checkboxes
    // seed their initialValues — and it exposes no value-vs-initial comparison. See handleChange.
    const initialFormValuesRef = useRef({});
    useEffect(() => {
        if (props.userInfo && props.customerConfig) {
            setCrsSensitiveData(getSensitiveData(props.userInfo, props.customerConfig));
            setProductsList(getProducts(props.userInfo, props.customerConfig));
            setStatusSensitiveData(getStatusSensitiveData(props.userInfo, props.customerConfig));
        }
    }, []);
    // Re-capture the clean baseline whenever the checkbox lists change. The permission sections mount
    // asynchronously (they render null until these lists are populated), and their SelectAll toggles seed
    // and re-settle their own values as they mount. This parent effect runs after those child field
    // effects of the same commit, so the snapshot is the fully-settled state — the banner stays hidden on
    // load. It also fires on bulk Enable/Clear (which change these lists); that re-baselines to the bulk
    // values, but `bulkChanged` independently keeps the banner up until save, so the banner is unaffected.
    useEffect(() => {
        initialFormValuesRef.current = JSON.parse(JSON.stringify(formApiRef.current?.getState().values ?? {}));
        setUnSavedData(false);
    }, [productsList, crsSensitiveData, statusSensitiveData]);
    // Builds a { fieldName: value } map covering every entitlement checkbox the form renders — the flat
    // permission fields, the "select all" group toggles, and the dynamic per-product / per-sensitive-field
    // checkboxes (keyed by productName / fieldText, matching their `field` props). Fed to formApi.setValues
    // for the bulk Enable/Clear actions; setValues only touches registered fields and leaves the rest alone.
    const buildBulkFieldValues = (value) => {
        const values = {
            productAll: value,
            sensitiveAll: value,
            statusSensitiveAll: value,
            createMyReport: value,
            viewStatus: value,
            viewCorrespondence: value,
            viewPayments: value,
            viewClaimNotes: value,
            viewLeaveNotes: value,
            viewEmployeeDetails: value,
            viewBasicAsRequest: value,
            viewFullAsRequest: value,
            viewAsCorrespondence: value,
            asNotes: value,
        };
        props.customerConfig?.sharedProducts.forEach(product => {
            values[product.productName] = value;
        });
        props.customerConfig?.sharedFields.forEach(field => {
            values[field.fieldText] = value;
        });
        return values;
    };
    // Recomputes the product / sensitive-data checkbox lists from the current user model so that, after a
    // bulk action, child checkboxes reflect the correct checked state.
    const refreshCheckboxLists = () => {
        setCrsSensitiveData(getSensitiveData(userInformation, props.customerConfig));
        setProductsList(getProducts(userInformation, props.customerConfig));
        setStatusSensitiveData(getStatusSensitiveData(userInformation, props.customerConfig));
    };
    const getProductList = () => {
        const newProductList = [];
        props.customerConfig.sharedProducts.map(product => {
            newProductList.push(product.productName);
        });
        return newProductList;
    };
    const onViewStatusOptionChange = (event) => {
        userInformation.viewStatus = event.currentTarget.checked;
        if (event.currentTarget.checked === false) {
            userInformation.viewCorrespondence = false;
            userInformation.viewPayments = false;
            userInformation.viewClaimNotes = false;
            userInformation.viewLeaveNotes = false;
            userInformation.viewEmployeeDetails = false;
            userInformation.viewBasicAsRequest = false;
            userInformation.viewFullAsRequest = false;
            userInformation.viewAsCorrespondence = false;
            userInformation.asNotes = false;
            // Disabled-gating is now reactive (ViewStatus reads viewStatus from informed) and there is no
            // remount to re-seed the child fields, so clear their informed values here to keep the visual
            // checkbox state consistent with the model we just cleared. Merge-only: other fields are untouched.
            formApiRef.current?.setValues({
                viewCorrespondence: false,
                viewPayments: false,
                viewClaimNotes: false,
                viewLeaveNotes: false,
                viewEmployeeDetails: false,
                viewBasicAsRequest: false,
                viewFullAsRequest: false,
                viewAsCorrespondence: false,
                asNotes: false,
            });
        }
    };
    // Re-derive the All Permissions Control label from the current working model. Called whenever an
    // individual edit lands so the moment any permission stops being fully maxed the button flips back to
    // "Enable All Permissions" (and re-maxing the last outstanding permission flips it to "Clear All
    // Permissions").
    const recomputeAllMaxed = () => {
        setAllMaxed(props.customerConfig
            ? isAllPermissionsMaxed(userInformation, props.customerConfig, props.permissions)
            : false);
    };
    // A field's own onChange mutates the shared userInformation model, but informed can fire this
    // form-level onChange before that mutation runs. So we don't recompute inline (it would read the
    // stale, pre-edit model); instead we bump a tick and re-derive in a post-commit effect, which runs
    // after every synchronous onChange handler for the event has completed.
    //
    // NOTE: this changeTick indirection is a known temporary workaround, not the chosen end state. It
    // exists only because recomputeAllMaxed reads the mutated userInformation model instead of informed's
    // own values — the same root coupling as the deferred follow-up ticket (Ryan #7). Once allMaxed
    // derives from useFormState() (subs/locs + sensitive access levels folded into real form fields) and
    // save stops reading userInformation, the tick and its effect come out. Tracked in the #7 follow-up.
    const handleChange = () => {
        // Derive unsaved-state as a diff of the current field values against the clean baseline. We compare
        // only the fields present in the baseline: honest's CheckboxGroup lazily registers an aggregate
        // group field (e.g. `productGroup: []`) the first time a child is toggled, and that key is absent
        // from the settled baseline — a whole-object equality check would therefore never match again once
        // the user interacts, so the banner would stick forever. The individual checkbox booleans (which are
        // all in the baseline) fully capture the real state, so toggling a field back clears the banner.
        const values = formApiRef.current?.getState().values ?? {};
        const baseline = initialFormValuesRef.current;
        setUnSavedData(Object.keys(baseline).some(field => !isEqual(values[field], baseline[field])));
        setChangeTick(tick => tick + 1);
    };
    useEffect(() => {
        recomputeAllMaxed();
    }, [changeTick]);
    // The Subs/Locs modal persists its own change independently of the main form Save, so it reports the
    // saved access level back here; mirror it onto the working model and re-derive the control's label.
    const handleSubsLocsChange = (subsidiariesAndLocations) => {
        userInformation.subsidiariesAndLocations = subsidiariesAndLocations;
        recomputeAllMaxed();
    };
    const saveUserPermissions = async () => {
        try {
            trackEvent({
                event_action: TEALIUM_EVENT.EVENT_ACTION.BUTTON,
                event_type: TEALIUM_EVENT.EVENT_TYPE.EMPLOYER_PERMISSIONS_FORM,
                event_name: 'Save Employer Permissions',
            });
            const requestBody = {
                userId: userInformation.userId,
                products: userInformation.products,
                customerReportingSolution: userInformation.customerReportingSolution,
                sensitiveData: userInformation.sensitiveData,
                viewStatus: userInformation.viewStatus,
                viewCorrespondence: userInformation.viewCorrespondence,
                viewPayments: userInformation.viewPayments,
                viewClaimNotes: userInformation.viewClaimNotes,
                viewLeaveNotes: userInformation.viewLeaveNotes,
                viewEmployeeDetails: userInformation.viewEmployeeDetails,
                viewBasicAsRequest: userInformation.viewBasicAsRequest,
                viewFullAsRequest: userInformation.viewFullAsRequest,
                viewAsCorrespondence: userInformation.viewAsCorrespondence,
                asNotes: userInformation.asNotes,
            };
            if (bulkChanged) {
                requestBody.subsidiariesAndLocations = userInformation.subsidiariesAndLocations;
            }
            await updateEmployerUser(authContext.authHeader(), requestBody);
            // A bulk action lands subs/locs on FULL or NONE; clear any stale granular crs-service selection
            // so the access level remains the single source of truth (applies to both Enable All and Clear All).
            if (bulkChanged &&
                (userInformation.subsidiariesAndLocations === AccessLevelEnum.FULL ||
                    userInformation.subsidiariesAndLocations === AccessLevelEnum.NO_ACCESS)) {
                await clearEmployerSubsLocs(authContext.authHeader(), userInformation.userId);
            }
        }
        catch (error) {
            setErrorMessage('Error saving user permissions');
        }
        setClearAll(false);
        setBulkChanged(false);
        setChanged(false);
        setUnSavedData(false);
        // The just-saved values become the new clean baseline, so subsequent edits diff against them.
        initialFormValuesRef.current = JSON.parse(JSON.stringify(formApiRef.current?.getState().values ?? {}));
        setShowAlert(true);
    };
    const cancelChanges = () => {
        trackEvent({
            event_action: TEALIUM_EVENT.EVENT_ACTION.BUTTON,
            event_type: TEALIUM_EVENT.EVENT_TYPE.EMPLOYER_PERMISSIONS_FORM,
            event_name: 'Undo Employer Permissions',
        });
        props.onCancel();
        setClearAll(false);
        setBulkChanged(false);
        setChanged(false);
        setUnSavedData(false);
    };
    const closeAlert = () => {
        setErrorMessage('');
        setShowAlert(false);
    };
    const clearAllSelection = async () => {
        trackEvent({
            event_action: TEALIUM_EVENT.EVENT_ACTION.BUTTON,
            event_type: TEALIUM_EVENT.EVENT_TYPE.USER_ACCESS,
            event_name: 'clear all permissions',
            page_l3: TEALIUM_PAGE_VIEW.PAGE_L3.USER_ACCESS,
        });
        setUserPermissionState(userInformation);
        refreshCheckboxLists();
        setClearAll(true);
        setBulkChanged(true);
        setAllMaxed(false);
        setUnSavedData(true);
        // Push the cleared values onto the form. Disabled-gating is reactive to these (Crs/ViewStatus read
        // from informed), so no remount is needed to re-read state.
        formApiRef.current?.setValues(buildBulkFieldValues(false));
    };
    // Inverse of clearAllSelection: drive every entitled MLP permission to its maximum value without saving.
    const enableAllSelection = async () => {
        trackEvent({
            event_action: TEALIUM_EVENT.EVENT_ACTION.BUTTON,
            event_type: TEALIUM_EVENT.EVENT_TYPE.USER_ACCESS,
            event_name: 'select all permissions',
            page_l3: TEALIUM_PAGE_VIEW.PAGE_L3.USER_ACCESS,
        });
        setUserPermissionStateToFull(userInformation, props.customerConfig, props.permissions);
        refreshCheckboxLists();
        setClearAll(false);
        setBulkChanged(true);
        setAllMaxed(true);
        setUnSavedData(true);
        // Push the maxed values onto the form. Disabled-gating is reactive to these (Crs/ViewStatus read
        // from informed), so no remount is needed to re-read state.
        formApiRef.current?.setValues(buildBulkFieldValues(true));
    };
    return (<>
      <AdminPermissionContext.Consumer>
        {() => (<AccessibleForm apiRef={formApiRef} onChange={handleChange}>
            <UnSavedData unSavedData={unSavedData || bulkChanged} showDisclaimer={allMaxed}/>
            <Row className={'mb-1'}>
              <Col sm={6} md={6} lg={6}/>
              <Col sm={6} md={6} lg={6} className={'d-flex justify-content-end'}>
                {/* Placeholder button pending UX-finalised All Permissions Control. */}
                <Button type={'button'} buttonType={'secondary'} size={'small'} data-testid={'allPermissionsControl'} aria-pressed={allMaxed} onClick={() => (allMaxed ? clearAllSelection() : enableAllSelection())}>
                  {allMaxed ? 'Clear All Permissions' : 'Enable All Permissions'}
                </Button>
              </Col>
            </Row>
            <Products products={productsList} userInfo={userInformation} customerConfig={props.customerConfig}/>
            {userInformation && props.customerConfig && <>
              <EmployerSubsAndLocs userInfo={props.userInfo} products={getProductList()} clearAll={clearAll} subsidiariesAndLocations={userInformation.subsidiariesAndLocations} onSubsLocsChange={handleSubsLocsChange}/>
                <Crs products={productsList} sensitiveData={crsSensitiveData} userInfo={userInformation} customerConfig={props.customerConfig} permissions={props.permissions}/>
                <ViewStatus onViewStatusOptionChange={onViewStatusOptionChange} statusSensitiveData={statusSensitiveData} userInformation={userInformation} customerConfig={props.customerConfig} permissions={props.permissions}/>
              </>}
            <SaveUndoButton saveUserPermissions={saveUserPermissions} cancelChanges={cancelChanges} changed={changed} closeAlert={closeAlert} errorMessage={errorMessage} showAlert={showAlert}/>
          </AccessibleForm>)}
      </AdminPermissionContext.Consumer>
    </>);
};
export default EmployerPermissions;
